import type { FrageAdressat } from './types';

/**
 * Empfaenger je Adressat — „Adressat → Person" (Plan Stufe 2.3).
 *
 * Jede Frage traegt ihre Adressaten als Rollenkuerzel (BH, OP, TWP, …). Wer
 * diese Rolle in DIESEM Projekt ausfuellt, steht in EINER Zuordnung je
 * Planungsphase (`planungsphasen.empfaenger`, jsonb, Migration Nr. 162):
 * Adressat → Name, E-Mail, Herkunft, ggf. Kontakt-Id.
 *
 * Hier liegen die REGELN, ohne React und ohne Datenbank (`empfaenger.test.ts`):
 *   - die Vorschlaege (Bauherr aus den Projektstammdaten, Planer und
 *     Gutachter ueber Rolle und Gewerk der Verteilerkontakte),
 *   - die Normalisierung dessen, was aus der jsonb-Spalte kommt,
 *   - der Schreiber der Einzelaenderungen mit den Wettlaufregeln aus
 *     `unterlagenSpeicher.ts` (geschrieben wird je Adressat, mit Sperre auf
 *     `updated_at` — siehe `aendereEmpfaenger` im Service).
 *
 * NICHTS WIRD AUTOMATISCH GESETZT. Ein Vorschlag ist ein Knopf; mehrere
 * Treffer stehen nebeneinander, der Mensch waehlt. Freitext bleibt immer
 * moeglich. Beim Versand (Stufe 4) werden aus der Zuordnung
 * `planungsfragen.empfaenger_name` / `empfaenger_email` gefuellt — bis dahin
 * zeigt die Fragenliste den Namen nur an.
 *
 * Der Typ steht bewusst HIER und nicht in `types.ts`: die Datei wird von
 * jedem Reiter des Moduls gelesen, und die Zuordnung braucht nur die
 * Fragenliste.
 */

/** Woher ein Eintrag stammt. `kontakt` = Projektverteiler, `bauherr` =
 *  Projektstammdaten (clientName/clientEmail), `hand` = frei eingetippt. */
export type EmpfaengerQuelle = 'kontakt' | 'bauherr' | 'hand';

export interface EmpfaengerEintrag {
    name: string;
    email?: string;
    /** `Contact.id` aus dem Projektverteiler, wenn aus einem Kontakt uebernommen. */
    kontaktId?: string;
    quelle: EmpfaengerQuelle;
}

/** Die Zuordnung einer Planungsphase, so wie sie in `planungsphasen.empfaenger` steht. */
export type EmpfaengerZuordnung = Partial<Record<FrageAdressat, EmpfaengerEintrag>>;

/** Alle Adressaten in Anzeigereihenfolge (wie `FrageAdressat` und der CHECK in Nr. 39). */
export const ADRESSATEN: readonly FrageAdressat[] = ['BH', 'OP', 'TWP', 'TGA', 'FP', 'GUT', 'BEH', 'AN', 'BET'];

const QUELLEN: readonly EmpfaengerQuelle[] = ['kontakt', 'bauherr', 'hand'];

/** Projektstammdaten, soweit die Vorschlaege sie brauchen (Ausschnitt aus `ReportMetadata`). */
export interface ProjektStammdaten {
    clientName?: string | null;
    clientContactPerson?: string | null;
    clientEmail?: string | null;
}

/** Ein Kontakt des Projektverteilers, soweit die Vorschlaege ihn brauchen (Ausschnitt aus `Contact`). */
export interface KontaktQuelle {
    id: string;
    name: string;
    company?: string;
    role?: string;
    email?: string;
    tradeType?: string;
}

export interface EmpfaengerQuellen {
    stammdaten?: ProjektStammdaten | null;
    kontakte?: KontaktQuelle[] | null;
}

export interface EmpfaengerVorschlag {
    eintrag: EmpfaengerEintrag;
    /** Woran der Vorschlag haengt — fuer die Anzeige, damit der Klick begruendet ist. */
    grund: string;
}

// ═══════════════════════════════════════════════════════════════════════════
// STICHWORTE — Rolle und Gewerk der Kontakte
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Stichworte je Adressat, geprueft gegen die ROLLE des Kontakts
 * (`Contact.role`, freier Text; Vorgaben in `ContactsView.DEFAULT_ROLES`:
 * Bauherr, SiGeKo, Bauleitung, Architekt, Statiker, Gewerk, Fachplaner,
 * Behoerde). Verglichen wird je WORT als Anfang (siehe `trifft`): „bauherr"
 * trifft „Bauherr" und „Bauherrenvertreter", „statik" trifft „Statiker",
 * „amt" trifft „Amt" und „Amtsleiter", aber nicht „Gesamt".
 *
 * „Bauleitung" steht absichtlich nirgends: sie kann auf der Seite des
 * Objektplaners (LPH 8) oder des Unternehmers stehen — das entscheidet der
 * Mensch, nicht ein Stichwort.
 */
const ROLLEN_STICHWORTE: Record<FrageAdressat, readonly string[]> = {
    BH: ['bauherr', 'auftraggeber', 'eigentuemer', 'projektsteuer', 'investor'],
    OP: ['architekt', 'objektplan', 'generalplan', 'entwurfsverfass', 'objektueberwach'],
    TWP: ['statik', 'tragwerk', 'pruefstatik', 'pruefingenieur'],
    TGA: ['tga', 'gebaeudetechnik', 'versorgungstechnik', 'elektroplan', 'hls', 'hlsk', 'heizungsplan'],
    FP: ['fachplan', 'brandschutz', 'freianlagen', 'landschaftsarchitekt', 'verkehrsplan', 'bauphysik', 'schallschutz', 'vermess'],
    GUT: ['gutachter', 'gutachten', 'sachverstaend', 'baugrund', 'geotechn', 'bodengutacht', 'schadstoff', 'kampfmittel', 'umweltgutacht'],
    BEH: ['behoerde', 'amt', 'bauaufsicht', 'bauordnungsamt', 'gewerbeaufsicht', 'versorger', 'stadtwerke', 'netzbetreiber', 'feuerwehr', 'denkmal'],
    AN: ['gewerk', 'ausfuehrend', 'nachunternehm', 'generalunternehm', 'bauunternehm', 'polier', 'unternehmer'],
    BET: ['betreiber', 'nutzer', 'mieter', 'hausmeister', 'facility', 'objektverwalt', 'hausverwalt'],
};

/**
 * Stichworte je Adressat, geprueft gegen das GEWERK des Kontakts
 * (`Contact.tradeType`, Liste `ContactsView.STANDARD_TRADES`). Ein Gewerk
 * nennt zunaechst eine AUSFUEHRENDE Firma — ein Elektriker ist kein
 * TGA-Planer. Deshalb zaehlt das Gewerk nur, wenn die Rolle eine planende
 * oder beratende ist (`PLANENDE_ROLLEN`): „Fachplaner" + „Elektroinstallation"
 * → TGA, „Fachplaner" + „Garten- & Landschaftsbau" → FP.
 */
const GEWERK_STICHWORTE: Partial<Record<FrageAdressat, readonly string[]>> = {
    TGA: ['elektro', 'heizung', 'sanitaer', 'hls', 'kaelte', 'klima', 'lueftung', 'aufzug'],
    FP: ['garten', 'landschaft', 'strassenbau', 'brandschutz', 'verkehr'],
    GUT: ['kampfmittel', 'baugrund', 'schadstoff'],
};

const PLANENDE_ROLLEN: readonly string[] = [
    'fachplan', 'planer', 'planung', 'ingenieur', 'ing', 'buero', 'architekt',
    'gutachter', 'sachverstaend', 'berater', 'beratung',
];

/** Kleinschreibung, Umlaute als ae/oe/ue/ss, uebrige Akzente weg. */
const normalisiere = (s: string): string =>
    s.toLowerCase()
        .replace(/ä/g, 'ae').replace(/ö/g, 'oe').replace(/ü/g, 'ue').replace(/ß/g, 'ss')
        .normalize('NFD').replace(/[\u0300-\u036f]/g, '');

/** Die Woerter eines Textes — alles, was keine Buchstaben/Ziffern sind, trennt. */
const woerter = (s: string | null | undefined): string[] =>
    normalisiere(s || '').split(/[^a-z0-9]+/).filter(Boolean);

/** Trifft eines der Stichworte den ANFANG eines Wortes? Liefert das Stichwort. */
const trifft = (text: string | null | undefined, stichworte: readonly string[]): string | null => {
    const w = woerter(text);
    for (const stichwort of stichworte) {
        if (w.some(wort => wort.startsWith(stichwort))) return stichwort;
    }
    return null;
};

const sauber = (s: string | null | undefined): string => (s || '').trim();

/** Anzeigename eines Kontakts: „Name (Firma)", sonst was da ist. */
export const kontaktName = (k: Pick<KontaktQuelle, 'name' | 'company'>): string => {
    const name = sauber(k.name);
    const firma = sauber(k.company);
    if (name && firma) return `${name} (${firma})`;
    return name || firma;
};

/** Zwei Eintraege meinen dieselbe Person, wenn Name und E-Mail (normalisiert) gleich sind. */
export const gleicherEmpfaenger = (
    a: Pick<EmpfaengerEintrag, 'name' | 'email'> | null | undefined,
    b: Pick<EmpfaengerEintrag, 'name' | 'email'> | null | undefined,
): boolean => {
    if (!a || !b) return false;
    return normalisiere(sauber(a.name)) === normalisiere(sauber(b.name))
        && sauber(a.email).toLowerCase() === sauber(b.email).toLowerCase();
};

/** Zwei Eintraege sind gleich, wenn die GESPEICHERTEN Felder (Name, E-Mail,
 *  Herkunft, Kontakt-Id) zeichengleich sind — nur Rand-Leerraum zaehlt nicht;
 *  zweimal „niemand" (null/undefined) ist ebenfalls gleich.
 *
 *  Bewusst NICHT normalisiert wie `gleicherEmpfaenger` (das bleibt fuer das
 *  Entdoppeln der Vorschlaege): Schreib- und Konfliktpruefung haengen hieran.
 *  Normalisiert galt „Muller" → „Müller" als unveraendert — die Korrektur
 *  wurde nicht gespeichert, und eine fremde Korrektur dieser Art blieb im
 *  Konfliktvergleich unentdeckt. */
export const gleicherEintrag = (
    a: EmpfaengerEintrag | null | undefined,
    b: EmpfaengerEintrag | null | undefined,
): boolean => {
    if (!a || !b) return !a && !b;
    return sauber(a.name) === sauber(b.name)
        && sauber(a.email) === sauber(b.email)
        && a.quelle === b.quelle
        && sauber(a.kontaktId) === sauber(b.kontaktId);
};

// ═══════════════════════════════════════════════════════════════════════════
// VORSCHLAEGE
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Die Vorschlaege fuer EINEN Adressaten. Reihenfolge: zuerst die
 * Projektstammdaten (nur BH), dann die Kontakte in Verteilerreihenfolge.
 * Dieselbe Person (Name + E-Mail) erscheint nur einmal; Kontakte ohne
 * Namen und Firma fallen weg. Leer, wenn nichts passt — dann bleibt der
 * Freitext.
 */
export const vorschlagEmpfaenger = (
    adressat: FrageAdressat,
    quellen: EmpfaengerQuellen | null | undefined,
): EmpfaengerVorschlag[] => {
    const ergebnis: EmpfaengerVorschlag[] = [];
    const nimm = (v: EmpfaengerVorschlag) => {
        if (!v.eintrag.name) return;
        if (ergebnis.some(e => gleicherEmpfaenger(e.eintrag, v.eintrag))) return;
        ergebnis.push(v);
    };

    if (adressat === 'BH') {
        const s = quellen?.stammdaten;
        const firma = sauber(s?.clientName);
        const person = sauber(s?.clientContactPerson);
        const email = sauber(s?.clientEmail);
        const name = person && firma ? `${person} (${firma})` : (person || firma);
        if (name) {
            nimm({
                eintrag: { name, ...(email ? { email } : {}), quelle: 'bauherr' },
                grund: 'Bauherr laut Projektdaten',
            });
        }
    }

    const rollenStichworte = ROLLEN_STICHWORTE[adressat];
    const gewerkStichworte = GEWERK_STICHWORTE[adressat];
    for (const k of quellen?.kontakte || []) {
        if (!k) continue;
        const name = kontaktName(k);
        if (!name) continue;
        let grund: string | null = null;
        if (trifft(k.role, rollenStichworte)) {
            grund = `Rolle „${sauber(k.role)}"`;
        } else if (gewerkStichworte && trifft(k.role, PLANENDE_ROLLEN) && trifft(k.tradeType, gewerkStichworte)) {
            grund = `${sauber(k.role)} für „${sauber(k.tradeType)}"`;
        }
        if (!grund) continue;
        const email = sauber(k.email);
        nimm({
            eintrag: {
                name,
                ...(email ? { email } : {}),
                ...(sauber(k.id) ? { kontaktId: sauber(k.id) } : {}),
                quelle: 'kontakt',
            },
            grund,
        });
    }
    return ergebnis;
};

/** Die Adressaten, die in den Fragen vorkommen, in Anzeigereihenfolge. */
export const adressatenIn = (fragen: ReadonlyArray<{ adressat?: FrageAdressat[] | null }>): FrageAdressat[] => {
    const vorhanden = new Set<FrageAdressat>();
    fragen.forEach(f => (f.adressat || []).forEach(a => vorhanden.add(a)));
    return ADRESSATEN.filter(a => vorhanden.has(a));
};

// ═══════════════════════════════════════════════════════════════════════════
// EINGABE VON HAND
// ═══════════════════════════════════════════════════════════════════════════

/** Was an einer Eingabe von Hand nicht stimmt — oder `null`. Der Name ist
 *  Pflicht; eine E-Mail ist freiwillig, muss dann aber eine sein. */
export const pruefeEmpfaengerEingabe = (name: string, email: string): string | null => {
    if (!sauber(name)) return 'Bitte einen Namen eintragen.';
    const e = sauber(email);
    if (e && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e)) return 'Die E-Mail-Adresse ist nicht vollständig.';
    return null;
};

export const empfaengerVonHand = (name: string, email: string): EmpfaengerEintrag => {
    const e = sauber(email);
    return { name: sauber(name), ...(e ? { email: e } : {}), quelle: 'hand' };
};

/**
 * Setzt oder entfernt (`null`) den Eintrag eines Adressaten. Liefert
 * dieselbe Referenz, wenn sich nichts aendert — der Aufrufer schreibt dann
 * nicht.
 */
export const setzeEmpfaenger = (
    zuordnung: EmpfaengerZuordnung,
    adressat: FrageAdressat,
    eintrag: EmpfaengerEintrag | null,
): EmpfaengerZuordnung => {
    const alt = zuordnung[adressat];
    if (!eintrag) {
        if (!alt) return zuordnung;
        const neu = { ...zuordnung };
        delete neu[adressat];
        return neu;
    }
    if (gleicherEintrag(alt, eintrag)) return zuordnung;
    return { ...zuordnung, [adressat]: eintrag };
};

// ═══════════════════════════════════════════════════════════════════════════
// NORMALISIERUNG — was aus der jsonb-Spalte kommt
// ═══════════════════════════════════════════════════════════════════════════

const eintragAus = (v: unknown): EmpfaengerEintrag | null => {
    if (!v || typeof v !== 'object' || Array.isArray(v)) return null;
    const o = v as Record<string, unknown>;
    const name = typeof o.name === 'string' ? o.name.trim() : '';
    if (!name) return null;
    const email = typeof o.email === 'string' ? o.email.trim() : '';
    const kontaktId = typeof o.kontaktId === 'string' ? o.kontaktId.trim() : '';
    const quelle = QUELLEN.includes(o.quelle as EmpfaengerQuelle) ? (o.quelle as EmpfaengerQuelle) : 'hand';
    return {
        name,
        ...(email ? { email } : {}),
        ...(kontaktId ? { kontaktId } : {}),
        quelle,
    };
};

/**
 * Alles, was kein Objekt mit bekannten Adressaten und brauchbaren Eintraegen
 * ist, zaehlt als „nichts zugeordnet" — und nicht als Absturz in der ersten
 * Lesestelle (Hausregel (a) in `planungsphaseService.ts`). Unbekannte
 * Schluessel und Eintraege ohne Namen fallen weg; eine unbekannte Herkunft
 * wird zu `hand`.
 */
export const normalisiereEmpfaenger = (v: unknown): EmpfaengerZuordnung => {
    if (!v || typeof v !== 'object' || Array.isArray(v)) return {};
    const rein: EmpfaengerZuordnung = {};
    const o = v as Record<string, unknown>;
    for (const a of ADRESSATEN) {
        const e = eintragAus(o[a]);
        if (e) rein[a] = e;
    }
    return rein;
};

// ═══════════════════════════════════════════════════════════════════════════
// SCHREIBER — Einzelaenderungen, Wettlaufregeln wie unterlagenSpeicher.ts
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Geschrieben wird JE ADRESSAT, nicht die ganze Zuordnung: der Service
 * (`aendereEmpfaenger`) liest die Zeile frisch, setzt nur den einen
 * Adressaten und schreibt mit Sperre auf `updated_at` — zwei Sitzungen
 * loeschen sich so nicht mehr gegenseitig die Eintraege (Codex-Review P2).
 * Hat jemand DENSELBEN Adressaten dazwischen geaendert, schreibt der Service
 * nicht, sondern meldet `konflikt` mit dem fremden Stand.
 *
 * Der Schreiber haelt die Reihenfolge und Wiederholung der Einzelaenderungen
 * nach den vier Regeln der Unterlagen-Checkliste (`unterlagenSpeicher.ts`):
 *   1. hoechstens EIN Schreibvorgang unterwegs,
 *   2. die juengste Aenderung gewinnt — je Planungsphase UND Adressat:
 *      eine aeltere, noch nicht angefangene Aenderung desselben Adressaten
 *      entfaellt beim Vormerken; eine aeltere, die gerade unterwegs ist,
 *      entfaellt, sobald sie scheitert oder die juengere bestaetigt ist —
 *      sie darf nie wieder ueber den juengeren Stand geschrieben werden
 *      (Codex-Review Runde 2); andere Adressaten bleiben in Reihenfolge,
 *   3. ein Fehler verwirft nichts, solange keine juengere Aenderung desselben
 *      Adressaten wartet (dann traegt die juengere die Absicht); andere
 *      Adressaten haelt er nicht auf, sie sind serverseitig unabhaengig,
 *   4. Phasen verdraengen einander nie.
 * Ein `konflikt` nimmt ALLE wartenden Aenderungen desselben Adressaten mit
 * heraus: der Mensch sieht den fremden Wert und entscheidet neu.
 * Die Vergleichsbasis eines Auftrags (Zieladressat beim ersten Lesen) bleibt
 * ueber Fehler hinweg am Auftrag (`basis`) und geht bei jedem weiteren
 * Versuch mit — sonst ueberschriebe ein spaeterer Anstoss mit frisch
 * gelesener Basis eine fremde Aenderung ohne Konflikt (Codex Runde 3).
 * Nach jedem bestaetigten Schreiben (auch nach einem Konflikt) meldet
 * `uebernommen` den SERVERSTAND samt dem, was fuer die Phase noch aussteht —
 * der Hook legt beides uebereinander (`wendeAn`).
 * Ohne Entprellung: gesetzt wird per Klick, nicht beim Tippen.
 */
export type EmpfaengerSpeicherstand = 'gespeichert' | 'speichert' | 'fehler';

/** Der Zieladressat beim ERSTEN Lesen eines Auftrags; `eintrag: null` = niemand war zugeordnet. */
export interface EmpfaengerBasis {
    eintrag: EmpfaengerEintrag | null;
}

/**
 * Fehler eines Schreibvorgangs (`aendereEmpfaenger`), der die Vergleichsbasis
 * mitnimmt, sobald sie gelesen war — der Schreiber haelt sie am Auftrag fest.
 * `basis === null`: der Fehler kam, bevor etwas gelesen war.
 */
export class EmpfaengerSchreibFehler extends Error {
    readonly basis: EmpfaengerBasis | null;

    constructor(message: string, basis: EmpfaengerBasis | null) {
        super(message);
        this.name = 'EmpfaengerSchreibFehler';
        this.basis = basis;
    }
}

export interface EmpfaengerAenderung {
    phaseId: string;
    adressat: FrageAdressat;
    /** `null` = Zuordnung entfernen. */
    eintrag: EmpfaengerEintrag | null;
    /**
     * Der Zieladressat beim ERSTEN Lesen dieses Auftrags — die Vergleichsbasis
     * fuer „hat sich jemand anderes genau daran vergriffen". Der Schreiber
     * haelt sie nach dem ersten Versuch am Auftrag fest (auch nach Fehlern)
     * und gibt sie bei JEDEM weiteren Versuch mit, auch bei „Erneut
     * speichern"; sonst laese ein spaeterer Anstoss eine NEUE Basis und
     * ueberschriebe eine zwischenzeitliche fremde Aenderung am Zieladressaten
     * ohne Konfliktmeldung (Codex-Review Runde 3). `undefined` = noch nie
     * gelesen.
     */
    basis?: EmpfaengerBasis;
}

/**
 * Ergebnis EINES Schreibvorgangs (`aendereEmpfaenger`):
 *   geschrieben  — die Aenderung steht jetzt in der Datenbank,
 *   unveraendert — sie stand dort schon so, nichts geschrieben,
 *   konflikt     — jemand anderes hat DENSELBEN Adressaten dazwischen
 *                  geaendert; nichts geschrieben, `zuordnung` ist sein Stand.
 */
export type EmpfaengerSchreibStatus = 'geschrieben' | 'unveraendert' | 'konflikt';

export interface EmpfaengerSchreibErgebnis {
    status: EmpfaengerSchreibStatus;
    /** Was die Datenbank danach traegt. */
    zuordnung: EmpfaengerZuordnung;
}

export interface EmpfaengerSchreiber {
    /** Aenderung vormerken und sofort schreiben (bzw. nach dem laufenden Durchgang). */
    vormerken: (aenderung: EmpfaengerAenderung) => void;
    /** Jetzt schreiben, was aussteht („Erneut speichern"). */
    speichern: () => Promise<void>;
    /** Noch nicht bestaetigte Aenderungen einer Phase in Reihenfolge — vorgemerkt,
     *  gescheitert oder gerade unterwegs. Nur lesend (Kopie). */
    ausstehendFuer: (phaseId: string) => EmpfaengerAenderung[];
}

/** Legt ausstehende Aenderungen in Reihenfolge auf einen Stand. Unveraendert → dieselbe Referenz. */
export const wendeAn = (
    zuordnung: EmpfaengerZuordnung,
    aenderungen: ReadonlyArray<EmpfaengerAenderung>,
): EmpfaengerZuordnung =>
    aenderungen.reduce((stand, a) => setzeEmpfaenger(stand, a.adressat, a.eintrag), zuordnung);

export const erzeugeEmpfaengerSchreiber = (opt: {
    /** Schreibt EINE Aenderung; liefert Status und Serverstand der Phase danach. */
    schreibe: (aenderung: EmpfaengerAenderung) => Promise<EmpfaengerSchreibErgebnis>;
    /** Nach jedem bestaetigten Schreibvorgang (auch Konflikt): die Aenderung, das
     *  Ergebnis mit dem Serverstand und was fuer die Phase noch aussteht. */
    uebernommen: (
        aenderung: EmpfaengerAenderung,
        ergebnis: EmpfaengerSchreibErgebnis,
        ausstehend: EmpfaengerAenderung[],
    ) => void;
    /** `fehler` undefined = Meldung unveraendert lassen. */
    melde: (stand: EmpfaengerSpeicherstand, fehler?: string | null) => void;
    fehlerText: (e: unknown) => string;
}): EmpfaengerSchreiber => {
    /** Je Phase die Aenderungen in Reihenfolge — die gerade unterwegs
     *  befindliche bleibt darin, bis sie bestaetigt ist. */
    const ausstehend = new Map<string, EmpfaengerAenderung[]>();
    let unterwegs: EmpfaengerAenderung | null = null;
    let laeuft = false;

    const liste = (phaseId: string): EmpfaengerAenderung[] => ausstehend.get(phaseId) || [];
    const setzeListe = (phaseId: string, l: EmpfaengerAenderung[]) => {
        if (l.length) ausstehend.set(phaseId, l);
        else ausstehend.delete(phaseId);
    };
    const offen = (): number => Array.from(ausstehend.values()).reduce((n, l) => n + l.length, 0);

    const naechste = (gescheitert: Set<EmpfaengerAenderung>): EmpfaengerAenderung | null => {
        for (const l of ausstehend.values()) {
            const a = l.find(x => !gescheitert.has(x));
            if (a) return a;
        }
        return null;
    };

    /** Nach einer Bestaetigung: die Aenderung selbst und alle AELTEREN desselben
     *  Adressaten entfallen; bei Konflikt auch die juengeren — der Mensch
     *  entscheidet gegen den fremden Wert neu. */
    const raeumeAuf = (a: EmpfaengerAenderung, auchJuengere: boolean) => {
        const l = liste(a.phaseId);
        const i = l.indexOf(a);
        setzeListe(a.phaseId, l.filter((x, j) => x !== a && !(x.adressat === a.adressat && (auchJuengere || j < i))));
    };

    const speichern = async (): Promise<void> => {
        // Der laufende Durchgang nimmt neue Aenderungen nach seinem Ende mit.
        if (laeuft) return;
        laeuft = true;
        // In DIESEM Durchgang gescheiterte Aenderungen — sie werden nicht sofort
        // wiederholt (das liefe bei einem dauerhaften Fehler endlos), bleiben
        // aber vorgemerkt fuer den naechsten Anstoss.
        const gescheitert = new Set<EmpfaengerAenderung>();
        let letzterFehler: unknown = null;
        try {
            for (;;) {
                const a = naechste(gescheitert);
                if (!a) break;
                opt.melde('speichert');
                unterwegs = a;
                try {
                    const ergebnis = await opt.schreibe(a);
                    unterwegs = null;
                    raeumeAuf(a, ergebnis.status === 'konflikt');
                    opt.uebernommen(a, ergebnis, [...liste(a.phaseId)]);
                } catch (e) {
                    unterwegs = null;
                    letzterFehler = e;
                    // Die beim ersten Lesen ermittelte Basis bleibt am Auftrag —
                    // jeder weitere Versuch (auch „Erneut speichern") prueft gegen SIE.
                    if (!a.basis && e instanceof EmpfaengerSchreibFehler && e.basis) a.basis = e.basis;
                    const l = liste(a.phaseId);
                    const i = l.indexOf(a);
                    // Wartet schon eine JUENGERE Aenderung desselben Adressaten,
                    // traegt sie die Absicht — die gescheiterte entfaellt und
                    // kann nie mehr ueber den juengeren Stand geschrieben werden.
                    if (l.some((x, j) => j > i && x.adressat === a.adressat)) setzeListe(a.phaseId, l.filter(x => x !== a));
                    else gescheitert.add(a);
                }
            }
            // Was jetzt noch aussteht, ist in diesem Durchgang gescheitert.
            if (offen()) opt.melde('fehler', opt.fehlerText(letzterFehler));
            else opt.melde('gespeichert', null);
        } finally {
            laeuft = false;
        }
    };

    const vormerken = (a: EmpfaengerAenderung) => {
        // Eine noch nicht angefangene Aenderung desselben Adressaten entfaellt
        // (Regel 2); die gerade unterwegs befindliche bleibt — die neue folgt ihr.
        const rest = liste(a.phaseId).filter(x => x === unterwegs || x.adressat !== a.adressat);
        setzeListe(a.phaseId, [...rest, a]);
        void speichern();
    };

    const ausstehendFuer = (phaseId: string): EmpfaengerAenderung[] => [...liste(phaseId)];

    return { vormerken, speichern, ausstehendFuer };
};

// ═══════════════════════════════════════════════════════════════════════════
// LAGE — der angezeigte Stand des Hooks, als reine Uebergaenge
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Was die Fragenliste zeigt: der Stand EINER Phase. `bestaetigungen` zaehlt,
 * wie oft der Server seit dem letzten Phasenwechsel einen Stand bestaetigt
 * hat (Schreiben oder Konflikt). Daran erkennt die Leseantwort des
 * Phasenwechsels, ob sie noch gilt: sie startet immer bei 0 — ist inzwischen
 * etwas bestaetigt, ist sie aelter als der bestaetigte Stand und wird
 * verworfen (Codex-Review Runde 2: ein Rueckwechsel in die Phase waehrend
 * eines laufenden Schreibvorgangs lieferte sonst den Stand VOR dem Schreiben
 * und verdraengte den bestaetigten).
 */
export interface EmpfaengerLage {
    phaseId: string | null;
    zuordnung: EmpfaengerZuordnung;
    bestaetigungen: number;
    /** Adressat, dessen Zuordnung jemand anderes dazwischen geaendert hat —
     *  die Anzeige sagt es, bis der Mensch die Meldung schliesst. */
    konflikt: FrageAdressat | null;
}

export const leereLage = (phaseId: string | null): EmpfaengerLage =>
    ({ phaseId, zuordnung: {}, bestaetigungen: 0, konflikt: null });

/** Serverstand nach einem bestaetigten Schreibvorgang (auch Konflikt) + was
 *  noch aussteht. Fremde Phase → unveraendert. Ein Konflikt merkt den
 *  Adressaten fuer die Meldung. */
export const uebernehmeBestaetigung = (
    lage: EmpfaengerLage,
    aenderung: EmpfaengerAenderung,
    ergebnis: EmpfaengerSchreibErgebnis,
    ausstehend: ReadonlyArray<EmpfaengerAenderung>,
): EmpfaengerLage => (lage.phaseId !== aenderung.phaseId
    ? lage
    : {
        phaseId: aenderung.phaseId,
        zuordnung: wendeAn(ergebnis.zuordnung, ausstehend),
        bestaetigungen: lage.bestaetigungen + 1,
        konflikt: ergebnis.status === 'konflikt' ? aenderung.adressat : lage.konflikt,
    });

export const schliesseKonflikt = (lage: EmpfaengerLage): EmpfaengerLage =>
    (lage.konflikt === null ? lage : { ...lage, konflikt: null });

/** Leseantwort des Phasenwechsels: nur fuer diese Phase und nur, wenn seit
 *  ihrem Start nichts bestaetigt wurde — der bestaetigte Stand gewinnt. */
export const uebernehmeLesung = (
    lage: EmpfaengerLage,
    phaseId: string,
    gelesen: EmpfaengerZuordnung,
    ausstehend: ReadonlyArray<EmpfaengerAenderung>,
): EmpfaengerLage => (lage.phaseId !== phaseId || lage.bestaetigungen > 0
    ? lage
    : { ...lage, zuordnung: wendeAn(gelesen, ausstehend) });

/** Eine eigene Aenderung sofort anzeigen. Unveraendert → dieselbe Referenz. */
export const uebernehmeAenderung = (
    lage: EmpfaengerLage,
    phaseId: string,
    adressat: FrageAdressat,
    eintrag: EmpfaengerEintrag | null,
): EmpfaengerLage => {
    if (lage.phaseId !== phaseId) return lage;
    const zuordnung = setzeEmpfaenger(lage.zuordnung, adressat, eintrag);
    return zuordnung === lage.zuordnung ? lage : { ...lage, zuordnung };
};

/**
 * Die Riegel vor JEDEM Schreibweg (Setzen, Entfernen, „Erneut speichern"):
 * nicht in der Nur-Ansicht, nicht ohne Phase, nicht solange der Stand laedt
 * (es gibt nichts, worauf ein Klick aufsetzen koennte) und nicht mit
 * `hinweis` (Spalte/Tabelle fehlt oder Lesen gescheitert — dann ist unklar,
 * ob Schreiben ueberhaupt moeglich ist).
 */
export const darfSchreiben = (riegel: {
    gesperrt: boolean;
    planungId: string | null;
    laden: boolean;
    hinweis: string | null;
}): boolean => !riegel.gesperrt && !!riegel.planungId && !riegel.laden && !riegel.hinweis;
